import {
  Building2,
  Flame,
  Mail,
  Package,
  Phone,
  Snowflake,
  Thermometer,
  User,
  X,
} from 'lucide-react'

import { ExpandableText } from '@/shared/components/ExpandableText'
import { Badge } from '@/shared/components/ui/badge'
import { Dialog, DialogContent } from '@/shared/components/ui/dialog'

function WarmthBadge({ warmth }) {
  if (!warmth) return null

  if (warmth === 'hot') {
    return (
      <Badge className="gap-1 rounded-full border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700">
        <Flame className="size-3.5 text-orange-500" />
        Hot
      </Badge>
    )
  }

  if (warmth === 'cold') {
    return (
      <Badge className="gap-1 rounded-full border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700">
        <Snowflake className="size-3.5 text-sky-500" />
        Cold
      </Badge>
    )
  }

  if (warmth === 'warm') {
    return (
      <Badge className="gap-1 rounded-full border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
        <Thermometer className="size-3.5 text-amber-500" />
        Warm
      </Badge>
    )
  }

  return (
    <Badge className="rounded-full border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700 capitalize">
      {warmth}
    </Badge>
  )
}

function ProductItem({ item, index, isPartialDesc }) {
  const qty = Number(item?.quantity ?? item?.qty) || 0
  const unitVal =
    Number(item?.value ?? item?.unitPrice ?? item?.price ?? item?.rate) || 0
  const itemTotal = qty * unitVal

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:flex-row sm:items-start sm:justify-between transition-all w-full min-w-0">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-xs font-bold text-indigo-700 mt-0.5">
          {index + 1}
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="text-sm font-semibold text-slate-900 break-words">
            <ExpandableText text={item.product || '-'} maxLength={40} />
          </div>
          {item.description ? (
            <div className="text-xs text-slate-500 break-words">
              <ExpandableText text={item.description} maxLength={60} />
            </div>
          ) : isPartialDesc ? (
            <div className="text-[11px] text-slate-400 italic">
              No description
            </div>
          ) : null}
          <p className="text-[11px] text-slate-500 mt-1.5 pt-1 border-t border-slate-50 max-w-fit">
            Unit Value:{' '}
            <span className="font-medium text-slate-700">
              ₹{unitVal.toLocaleString('en-IN')}
            </span>
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto shrink-0 mt-1 sm:mt-0">
        <Badge
          variant="outline"
          className="rounded-full border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-xs font-semibold text-indigo-700 whitespace-nowrap"
        >
          Qty: {qty}
        </Badge>
        <Badge
          variant="outline"
          className="rounded-full border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 whitespace-nowrap"
        >
          Total: ₹{itemTotal.toLocaleString('en-IN')}
        </Badge>
      </div>
    </div>
  )
}

export function ViewProductsModal({ open, onOpenChange, lead }) {
  if (!lead) return null

  const products = lead.products || []
  const productsWithDesc = products.filter(
    (p) => typeof p.description === 'string' && p.description.trim().length > 0,
  )
  const productsWithoutDesc = products.filter(
    (p) =>
      !p.description ||
      typeof p.description !== 'string' ||
      p.description.trim().length === 0,
  )
  const isPartialDesc =
    products.length > 1 &&
    productsWithDesc.length > 0 &&
    productsWithoutDesc.length > 0

  const displayProducts = isPartialDesc
    ? [...productsWithDesc, ...productsWithoutDesc]
    : products

  const totalQuantity = products.reduce(
    (sum, item) => sum + (Number(item?.quantity ?? item?.qty) || 0),
    0,
  )
  const totalValue =
    products.reduce((sum, item) => {
      const qty = Number(item?.quantity ?? item?.qty) || 1
      const val =
        Number(item?.value ?? item?.unitPrice ?? item?.price ?? item?.rate) || 0
      return sum + qty * val
    }, 0) ||
    Number(
      lead?.value ?? lead?.dealValue ?? lead?.totalValue ?? lead?.amount,
    ) ||
    0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] w-[94vw] max-w-[560px] sm:max-w-[580px] overflow-y-auto overflow-x-hidden rounded-3xl border-0 p-5 sm:p-6 shadow-2xl [&>button]:hidden">
        <div className="w-full min-w-0 space-y-5">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
                <Building2 className="size-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-bold text-slate-900 truncate">
                  {lead.company || 'Lead Details'}
                </h3>
                <p className="text-xs text-slate-500">
                  Lead Details &amp; Contact Info
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="shrink-0 rounded-full p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            >
              <X className="size-5" />
            </button>
          </div>

          {/* Contact Details Section */}
          <div className="w-full min-w-0 rounded-2xl border border-slate-100 bg-slate-50/70 p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Contact Details
              </span>
              <div className="shrink-0">
                <WarmthBadge warmth={lead.leadWarmth} />
              </div>
            </div>

            <div className="space-y-2.5">
              {/* Contact Person */}
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-white text-slate-500 shadow-sm border border-slate-200">
                  <User className="size-4 text-indigo-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-medium text-slate-400 leading-none">
                    Contact Person
                  </p>
                  <p className="text-sm font-semibold text-slate-800 break-words mt-0.5">
                    {lead.name || '-'}
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-white text-slate-500 shadow-sm border border-slate-200">
                  <Phone className="size-4 text-emerald-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-medium text-slate-400 leading-none">
                    Phone Number
                  </p>
                  {lead.phone ? (
                    <a
                      href={`tel:${lead.phone}`}
                      className="text-sm font-semibold text-emerald-700 hover:underline break-all block mt-0.5"
                    >
                      {lead.phone}
                    </a>
                  ) : (
                    <p className="text-sm font-semibold text-slate-800 mt-0.5">
                      -
                    </p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-white text-slate-500 shadow-sm border border-slate-200">
                  <Mail className="size-4 text-blue-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-medium text-slate-400 leading-none">
                    Email Address
                  </p>
                  {lead.email ? (
                    <a
                      href={`mailto:${lead.email}`}
                      className="text-sm font-semibold text-blue-700 hover:underline break-all block mt-0.5"
                    >
                      {lead.email}
                    </a>
                  ) : (
                    <p className="text-sm font-semibold text-slate-800 mt-0.5">
                      -
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Products Section */}
          <div className="w-full min-w-0 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Package className="size-4 text-slate-500" />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Products ({products.length})
                </span>
              </div>

              {products.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                    Total Qty:&nbsp;
                    <strong className="text-slate-900">{totalQuantity}</strong>
                  </span>
                  <span className="inline-flex items-center rounded-lg bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                    Total Value:&nbsp;
                    <strong className="text-emerald-800">
                      ₹{totalValue.toLocaleString('en-IN')}
                    </strong>
                  </span>
                </div>
              )}
            </div>

            {displayProducts.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-2">
                No products added for this lead.
              </p>
            ) : (
              <div className="space-y-2">
                {displayProducts.map((item, index) => (
                  <ProductItem
                    key={index}
                    item={item}
                    index={index}
                    isPartialDesc={isPartialDesc}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
